import React, { useEffect, useRef, useState } from "react";
import { X, Plus, Calendar, Trash2, Check } from "lucide-react";
import { MdExpandMore } from "react-icons/md";
import { BASE_URL } from "utils/constant";
import { useAlert } from "components/UI_components/Alert";

const getOnHandQuantity = (material) => {
  if (!material) return 0;
  const quantity = material?.current_stock;
  return quantity === null || quantity === undefined || quantity === ""
    ? 0
    : quantity;
};

const getOnHandLabel = (material) => {
  const quantity = getOnHandQuantity(material);
  return `${quantity}${material?.uom ? ` ${material.uom}` : ""}`;
};

/* --------- tiny helpers --------- */
const toDisplay = (isoDate /* yyyy-mm-dd */) => {
  if (!isoDate) return "";
  const [y, m, d] = isoDate.split("-");
  return `${d}/${m}/${y}`;
};
const toISO = (display /* dd/mm/yyyy */) => {
  if (!display || !display.includes("/")) return "";
  const [d, m, y] = display.split("/");
  if (!d || !m || !y) return "";
  return `${y.padStart(4, "0")}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
};

/* ===================== Material Selection Modal ===================== */
function MaterialSelectionModal({
  isOpen,
  onClose,
  onSelectMaterials,
  selectedMaterials = [],
  siteId,
}) {
  const [searchQuery, setSearchQuery] = useState("");
  const organisationId = JSON.parse(localStorage.getItem("organization"))?.id;
  const [availableMaterials, setAvailableMaterials] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);

  const getMaterials = async () => {
    const headers = {
      Authorization: `Bearer ${localStorage.getItem("access")}`,
      "Content-Type": "application/json",
    };

    const [libraryRes, siteMaterialsRes] = await Promise.all([
      fetch(`${BASE_URL}/org/material-library/`, {
        method: "POST",
        headers,
        body: JSON.stringify({ org_id: organisationId }),
      }),
      siteId
        ? fetch(`${BASE_URL}/inventory/get-materials-of-a-site/`, {
            method: "POST",
            headers,
            body: JSON.stringify({ sites: Number(siteId) }),
          })
        : Promise.resolve(null),
    ]);

    const libraryData = await libraryRes.json();
    const siteMaterialsData = siteMaterialsRes
      ? await siteMaterialsRes.json().catch(() => ({}))
      : { data: [] };

    const stockByMaterialId = new Map(
      (Array.isArray(siteMaterialsData?.data) ? siteMaterialsData.data : []).map(
        (item) => [item.material, item],
      ),
    );

    const mergedMaterials = (Array.isArray(libraryData) ? libraryData : []).map(
      (material) => ({
        ...material,
        current_stock: stockByMaterialId.get(material.id)?.current_stock ?? 0,
      }),
    );

    setAvailableMaterials(mergedMaterials);
  };

  useEffect(() => {
    if (isOpen) {
      setSelectedIds([]);
      getMaterials();
    }
  }, [isOpen, siteId]);

  const filteredMaterials = availableMaterials
    .filter((material) => {
      const name = (material?.material_name || "").toLowerCase();
      const sub = (material?.subcategory || "").toLowerCase();
      const q = searchQuery.toLowerCase();
      return name.includes(q) || sub.includes(q);
    })
    .sort((a, b) => {
      const stockA = Number(getOnHandQuantity(a)) || 0;
      const stockB = Number(getOnHandQuantity(b)) || 0;

      if (stockA !== stockB) {
        return stockA - stockB;
      }

      return (a?.material_name || "").localeCompare(b?.material_name || "");
    });

  const handleMaterialToggle = (material, alreadyInRequest) => {
    if (alreadyInRequest) return;
    setSelectedIds((prev) =>
      prev.includes(material.id)
        ? prev.filter((id) => id !== material.id)
        : [...prev, material.id]
    );
  };

  const handleAddSelected = () => {
    if (!selectedIds.length) return;
    const toAdd = availableMaterials.filter((m) => selectedIds.includes(m.id));
    onSelectMaterials(toAdd);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-lg max-h-[85vh] border border-gray-200 rounded-xl bg-white px-3 py-2 shadow-2xl flex flex-col">
        <div className="relative w-full flex flex-col flex-1 overflow-hidden">
        <div className="flex flex-row items-center py-2 justify-between border-b-[0.5px] border-[#A3A3A3] shrink-0">
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full flex items-center justify-center text-gray-600 hover:bg-gray-100"
          >
            <X size={20} />
          </button>

          <div className="text-lg font-normal flex flex-row items-center  text-[#232323]">
            Select Material
          </div>
          <div className=" flex w-[10%] flex-col items-center"></div>
        </div>

        <div className="flex-1 overflow-hidden py-2 flex flex-col">
          <div className="pb-2 mt-2 flex flex-col w-full items-left">
            <span className="text-sm font-normal pl-1 w-full text-[#232323]">
              Select from your material inventory
            </span>
            <div className="relative w-full flex flex-col items-left">
              <input
                type="text"
                placeholder="Search Your Material"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-4 py-1 w-[97%] ml-[2px] mt-1 text-base bg-[#F3F4F6] rounded-lg border-0 text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
              <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <circle cx="11" cy="11" r="8" />
                  <path d="m21 21-4.35-4.35" />
                </svg>
              </div>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto pb-4">
            <div className="space-y-1">
              {filteredMaterials.map((material) => {
                const alreadyInRequest = selectedMaterials.some(
                  (s) => s.id === material.id
                );
                const isNewlySelected = selectedIds.includes(material.id);
                const isSelected = alreadyInRequest || isNewlySelected;
                return (
                  <button
                    key={material.id}
                    onClick={() =>
                      handleMaterialToggle(material, alreadyInRequest)
                    }
                    className={`w-full flex items-center gap-3 px-2 py-1 border-b-[0.5px] border-[#C3C3C3] text-left transition
                      ${alreadyInRequest
                        ? "cursor-not-allowed opacity-70"
                        : isNewlySelected
                          ? "bg-purple-50"
                          : "hover:bg-gray-50"
                      }`}
                    disabled={alreadyInRequest}
                    aria-disabled={alreadyInRequest}
                    title={
                      alreadyInRequest
                        ? "Already added"
                        : isNewlySelected
                          ? "Click to remove"
                          : "Click to select"
                    }
                  >
                    <div
                      className={`w-5 h-5 rounded-full border-2 flex items-center justify-center
                        ${alreadyInRequest
                          ? "bg-gray-300 border-gray-300"
                          : isNewlySelected
                            ? "bg-[#3B0069] border-[#3B0069]"
                            : "border-[#232323]"
                        }`}
                    >
                      {isSelected && <Check size={12} className="text-white" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-[#232323]">
                        {material.material_name}
                      </div>
                      <div className="text-xs text-[#737373] mt-0.5">
                        In Stock: {getOnHandLabel(material)}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-auto">
                      {material?.specification && (
                        <span className="text-xs text-gray-700 bg-gray-100 px-2 py-1 rounded-full">
                          {material.specification}
                        </span>
                      )}
                      {material?.subcategory && (
                        <span className="text-xs text-gray-700 bg-gray-100 px-2 py-1 rounded-full">
                          {material.subcategory}
                        </span>
                      )}
                      {material?.uom && (
                        <span className="text-xs text-gray-700 bg-gray-100 px-2 py-1 rounded-full">
                          {material.uom}
                        </span>
                      )}
                    </div>
                  </button>
                );
              })}
              {filteredMaterials.length === 0 && (
                <div className="text-sm text-gray-500 p-4">
                  No materials found.
                </div>
              )}
            </div>
          </div>

          <div className="pt-2 border-t border-gray-100 flex items-center justify-between shrink-0">
            <span className="text-xs text-gray-500">
              {selectedIds.length
                ? `${selectedIds.length} material${selectedIds.length > 1 ? "s" : ""
                } selected`
                : "Select one or more materials to add"}
            </span>
            <button
              onClick={handleAddSelected}
              disabled={selectedIds.length === 0}
              className="px-4 py-1.5 rounded-lg text-sm font-medium text-white bg-[#3B0069] disabled:bg-gray-300 disabled:cursor-not-allowed hover:bg-[#3B0069D9] transition-colors"
            >
              Add Selected
            </button>
          </div>
        </div>
        </div>
      </div>
    </div>
  );
}

/* ===================== Main Component ===================== */
export default function NewMaterialRequest({
  onClose,
  onCreateMR,
  initialRequest = null,
  onUpdateMR,
}) {
  const isEditMode = Boolean(initialRequest?.id);
  const siteId = initialRequest?.sites || localStorage.getItem("site");
  const [deliveryDate, setDeliveryDate] = useState(
    new Date().toISOString().slice(0, 10).split("-").reverse().join("/")
  ); // dd/mm/yyyy
  const [materials, setMaterials] = useState([
    {
      id: null,
      material_name: "",
      specification: "",
      subcategory: "",
      quantity: "",
      uom: "",
    },
  ]);
  const [showMaterialSelector, setShowMaterialSelector] = useState(false);
  const [activeMaterialIndex, setActiveMaterialIndex] = useState(null);
  const hiddenDateInputRef = useRef(null);

  // NEW: submission states
  const [submitting, setSubmitting] = useState(false); // NEW
  const [errorMsg, setErrorMsg] = useState(""); // NEW
  const { addAlert } = useAlert();

  useEffect(() => {
    if (!isEditMode) return;

    setDeliveryDate(toDisplay(initialRequest?.due_date));
    const mappedMaterials = (initialRequest?.material_request_lines || []).map((line) => ({
      id: line.material?.id || line.material,
      line_id: line.id,
      material_name: line.material_name || "",
      specification: line.specification || line.material_spec || "",
      subcategory: line.subcategory || "",
      quantity: Number(line.quantity) || 1,
      uom: line.uom || "Unit",
    }));

    if (mappedMaterials.length > 0) {
      setMaterials(mappedMaterials);
    }
  }, [initialRequest, isEditMode]);

  const handleCalendarClick = () => {
    const iso = toISO(deliveryDate);
    if (hiddenDateInputRef.current && iso) {
      hiddenDateInputRef.current.value = iso;
    }
    if (hiddenDateInputRef.current?.showPicker) {
      hiddenDateInputRef.current.showPicker();
    } else {
      hiddenDateInputRef.current?.focus();
      hiddenDateInputRef.current?.click();
    }
  };

  const handleHiddenDateChange = (e) => {
    const iso = e.target.value;
    const display = toDisplay(iso);
    if (display) setDeliveryDate(display);
  };

  const handleAddMaterial = () => {
    setActiveMaterialIndex(null);
    setShowMaterialSelector(true);
  };

  const handleSelectMaterials = (selectedMaterialsList = []) => {
    if (!selectedMaterialsList.length) return;

    setMaterials((prev) => {
      const toRow = (material, existingRow = null) => ({
        id: material.id,
        line_id: existingRow?.line_id ?? null,
        material_name: material.material_name,
        specification: material.specification,
        subcategory: material.subcategory,
        quantity: existingRow?.quantity || 1,
        uom: material.uom || "Unit",
      });

      // Row-edit mode: replace clicked row with first selected, append remaining.
      if (activeMaterialIndex !== null) {
        const next = [...prev];
        const firstMaterial = selectedMaterialsList[0];
        next[activeMaterialIndex] = toRow(firstMaterial, next[activeMaterialIndex]);

        const existingIds = new Set(next.map((m) => m.id).filter(Boolean));
        const extras = selectedMaterialsList
          .slice(1)
          .filter((material) => !existingIds.has(material.id))
          .map((material) => toRow(material));

        return [...next, ...extras];
      }

      // Create/add mode: fill first empty row (if present) then append rest.
      let next = [...prev];
      let queue = [...selectedMaterialsList];
      const emptyIndex = next.findIndex((m) => !m.id);

      if (emptyIndex !== -1 && queue.length > 0) {
        next[emptyIndex] = toRow(queue[0], next[emptyIndex]);
        queue = queue.slice(1);
      }

      const existingIds = new Set(next.map((m) => m.id).filter(Boolean));
      const toAppend = queue
        .filter((material) => !existingIds.has(material.id))
        .map((material) => toRow(material));

      return [...next, ...toAppend];
    });

    setActiveMaterialIndex(null);
  };

  const handleRemoveMaterial = (index) => {
    setMaterials((prev) => prev.filter((_, i) => i !== index));
  };

  const handleQuantityChange = (index, quantity) => {
    setMaterials((prev) =>
      prev.map((material, i) =>
        i === index
          ? { ...material, quantity: Math.max(0, parseInt(quantity)) }
          : material
      )
    );
  };

  // NEW: API submit
  const handleSubmit = async () => {
    setErrorMsg("");
    // basic validation
    const dueISO = toISO(deliveryDate);
    if (!dueISO) {
      setErrorMsg("Please choose a valid delivery date.");
      return;
    }
    if (!materials.length) {
      setErrorMsg("Please add at least one material.");
      return;
    }

    for (const m of materials) {
      if (!m.id || !m.material_name) {
        setErrorMsg(`Please select a material.`);
        return;
      }
    }

    // every material should have quantity
    for (const m of materials) {
      if (!m.quantity || isNaN(m.quantity) || Number(m.quantity) <= 0) {
        setErrorMsg(`Please enter a valid quantity for ${m.material_name}.`);
        return;
      }
    }

    const payload = isEditMode
      ? {
        sites: initialRequest?.sites || localStorage.getItem("site"),
        due_date: dueISO,
        lines: materials.map((m) => ({
          ...(m.line_id ? { id: m.line_id } : {}),
          material: m.id,
          quantity: Number(m.quantity) || 1,
        })),
      }
      : {
        sites: localStorage.getItem("site"),
        due_date: dueISO, // yyyy-mm-dd
        status: "PENDING_APPROVAL",
        lines: materials.map((m) => ({
          material: m.id,
          quantity: Number(m.quantity) || 1,
        })),
      };

    try {
      setSubmitting(true);
      const res = await fetch(
        isEditMode
          ? `${BASE_URL}/inventory/material-requests/${initialRequest.id}/update/`
          : `${BASE_URL}/inventory/material-requests/create/`,
        {
          method: isEditMode ? "PUT" : "POST",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access")}`, // bearer token
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        // surface server-side message when available
        const msg =
          data?.detail ||
          data?.message ||
          (typeof data === "string"
            ? data
            : isEditMode
              ? "Failed to update request."
              : "Failed to create request.");
        throw new Error(msg);
      }

      if (isEditMode) {
        onUpdateMR?.(data);
      } else {
        onCreateMR?.(data);
      }
      onClose();
      addAlert(
        isEditMode
          ? "Material request updated successfully."
          : "Material request created successfully.",
        "success",
      );
    } catch (err) {
      setErrorMsg(err.message || "Something went wrong.");
      addAlert(err.message || "Something went wrong.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-50">
        <div className="absolute inset-0 bg-black/40" onClick={onClose} />

        <div className="absolute right-0 top-0 h-full w-full max-w-2xl bg-white border-l border-gray-200 shadow-2xl flex flex-col">
          <div className="flex items-center justify-between py-3 px-4 border-b border-gray-100 bg-white relative">
            <div></div>
            <h2 className="text-xl font-medium text-[#232323]">
              {isEditMode ? "Edit Material Request" : "New Material Request"}
            </h2>
            <button
              onClick={onClose}
              className=" w-8 h-8 rounded-full flex items-center justify-center text-gray-600 hover:bg-gray-100"
              aria-label="Close"
            >
              <X size={20} />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-6">
            {/* <div className="mb-6 flex flex-row items-center gap-4">
              <label className="block text-sm font-medium text-[#232323]">
                Material request No.
              </label>
              <div className="text-[#232323] font-normal text-sm text-center px-3 py-1 rounded-2xl bg-[#F3F4F6]">
                #MR_23-09-456
              </div>
            </div> */}

            <div className="mb-8">
              <label className="block text-sm font-medium text-[#232323] mb-2">
                Select Delivery Date
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={deliveryDate}
                  onChange={(e) => setDeliveryDate(e.target.value)}
                  onClick={handleCalendarClick}
                  className="w-full px-4 py-2 bg-[#F3F4F6] rounded-xl border-0 text-[#232323] focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="Select date"
                  readOnly
                />
                <button
                  type="button"
                  onClick={handleCalendarClick}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-600 hover:text-gray-800"
                  aria-label="Open calendar"
                >
                  <Calendar size={20} />
                </button>
              <input
                  ref={hiddenDateInputRef}
                  type="date"
                  onChange={handleHiddenDateChange}
                  className="sr-only absolute right-56 top-12 pointer-events-none"
                  min={isEditMode ? undefined : new Date().toISOString().slice(0, 10)}
                />
              </div>
            </div>

            <div className="mb-8">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-[#232323]">
                    Materials
                  </span>
                  <span className="bg-gray-200 text-gray-600 text-xs px-2 py-1 rounded-full min-w-[24px] text-center">
                    {materials.length}
                  </span>
                </div>
                <button
                  onClick={handleAddMaterial}
                  className="w-8 h-8 bg-[#ECE6F0] rounded-full flex items-center justify-center text-[#3B0069] hover:bg-purple-100 transition-colors"
                  title="Add material"
                >
                  <Plus size={16} />
                </button>
              </div>

              {showMaterialSelector && (
                <MaterialSelectionModal
                  isOpen={showMaterialSelector}
                  onClose={() => {
                    setShowMaterialSelector(false);
                    setActiveMaterialIndex(null);
                  }}
                  onSelectMaterials={handleSelectMaterials}
                  selectedMaterials={materials}
                  siteId={siteId}
                />
              )}

              <div className="space-y-3">
                {materials.map((material, index) => (
                  <div
                    key={`${material.id}-${index}`}
                    className="flex items-center justify-between w-full gap-3"
                  >
                    <div
                      className="flex flex-row cursor-pointer items-center w-[55%] justify-between px-3 py-2 rounded-lg bg-[#F3F4F6]"
                      onClick={() => {
                        setActiveMaterialIndex(index);
                        setShowMaterialSelector(true);
                      }}
                    >
                      <div className="w-full bg-transparent border-0 text-[#232323] font-normal focus:outline-none">
                        {material.material_name || "Select Material"}{" "}
                        {material.material_name ? (
                          <span className="text-xs text-gray-500">
                            ({material.material_name},{material.subcategory},{material.specification})
                          </span>
                        ) : null}
                      </div>
                      <span className="flex flex-col items-center justify-center">
                        <MdExpandMore color="#232323" size={24} />
                      </span>
                    </div>

                    <div className="flex flex-row items-center w-[35%] justify-between px-2 py-2 rounded-lg bg-[#F3F4F6]">
                      <input
                        type="number"
                        value={material.quantity}
                        placeholder="Qty"
                        onChange={(e) =>
                          handleQuantityChange(index, e.target.value)
                        }
                        style={{
                          WebkitAppearance: "textfield",
                          margin: 0,
                          MozAppearance: "textfield",
                        }}
                        className="w-[55%] text-center bg-transparent text-[#232323] font-normal focus:outline-none"
                        min="1"
                      />
                      <div className="border-l h-6 border-[#C3C3C3] mx-1" />
                      <div className="bg-transparent w-[30%] border-0 text-center text-[#232323] focus:outline-none">
                        {material?.uom ?? "Unit"}
                      </div>
                    </div>

                    <button
                      onClick={() => handleRemoveMaterial(index)}
                      className="w-8 h-8 text-red-500 hover:bg-red-50 bg-[#F9E7E7] rounded-full flex items-center justify-center transition-colors"
                      title="Remove"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
              </div>

              {/* NEW: inline error */}
              {errorMsg && (
                <div className="mt-4 text-sm text-red-600">{errorMsg}</div>
              )}
            </div>
          </div>

          <div className="sticky bottom-0 p-6 border-t flex flex-col items-center justify-center border-gray-100 bg-white">
            <button
              onClick={handleSubmit}
              disabled={submitting}
              style={{ alignSelf: "center", opacity: submitting ? 0.8 : 1 }}
              className="w-[40%] bg-[#3B0069] hover:bg-[#3B0069D9] disabled:cursor-not-allowed text-white font-medium py-2 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              {submitting
                ? (isEditMode ? "Updating..." : "Submitting...")
                : (isEditMode ? "Update Request" : "Material Request")}
              <span className="rounded-full bg-[#58267F] p-1 flex items-center justify-center">
                <Check size={18} />
              </span>
            </button>
          </div>
        </div>
      </div>

    </>
  );
}
